import { saveUserTargets, type UserTargets, userTargetsQueryKey } from "@/packages/dashboard/api/user-targets";
import { useUserTargets } from "@/packages/dashboard/UserTargets/UserTargetsProvider";
import { toOptionalNumber } from "@/packages/dashboard/utils/numeric-input-mantine-helper";
import { Alert, Button, Group, Loader, NumberInput, Paper, SimpleGrid, Stack, Text } from "@mantine/core";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { IconAlertCircle, IconCheck } from "@tabler/icons-react";
import type { FormEvent } from "react";
import { useEffect, useState } from "react";

type TargetsFormValues = UserTargets;

const EMPTY_TARGETS: TargetsFormValues = {
  caloriesDailyTarget: null,
  proteinsDailyTarget: null,
  carbsDailyTarget: null,
  fatsDailyTarget: null,
};

const caloriesFormatter = new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 });
const macrosFormatter = new Intl.NumberFormat(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });

type TargetMetric = {
  key: keyof TargetsFormValues;
  label: string;
  description: string;
  unit: string;
};

const targetMetrics: TargetMetric[] = [
  {
    key: "caloriesDailyTarget",
    label: "Calories",
    description: "Daily energy budget to anchor logging.",
    unit: "kcal / day",
  },
  {
    key: "proteinsDailyTarget",
    label: "Protein",
    description: "Grams per day to support recovery.",
    unit: "g / day",
  },
  {
    key: "carbsDailyTarget",
    label: "Carbs",
    description: "Grams per day to fuel training.",
    unit: "g / day",
  },
  {
    key: "fatsDailyTarget",
    label: "Fats",
    description: "Grams per day for hormones and satiety.",
    unit: "g / day",
  },
];

const formatMetricValue = (key: keyof TargetsFormValues, value: number | null) => {
  if (value === null || Number.isNaN(value)) {
    return "Not set";
  }

  const formatter = key === "caloriesDailyTarget" ? caloriesFormatter : macrosFormatter;
  return `${formatter.format(value)}`;
};

export const Targets = () => {
  const queryClient = useQueryClient();
  const { data: userTargets, isLoading: isLoadingTargets, error: fetchError } = useUserTargets();
  const [draftValues, setDraftValues] = useState<TargetsFormValues>(EMPTY_TARGETS);
  const [isEditing, setIsEditing] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (userTargets && !isEditing) {
      setDraftValues(userTargets);
    }
  }, [userTargets, isEditing]);

  const saveTargetsMutation = useMutation<UserTargets, Error, TargetsFormValues>({
    mutationFn: saveUserTargets,
    onSuccess: (updatedTargets) => {
      queryClient.setQueryData(userTargetsQueryKey, updatedTargets);
      setDraftValues(updatedTargets);
      setIsEditing(false);
      setSuccessMessage("Targets updated.");
      setErrorMessage(null);
    },
    onError: (mutationError) => {
      setErrorMessage(mutationError.message);
    },
  });

  const targets = userTargets ?? null;
  const isSaving = saveTargetsMutation.isPending;
  const showLoader = isLoadingTargets && !targets;
  const loadErrorMessage = fetchError?.message ?? null;

  const handleStartEdit = () => {
    if (!targets) return;
    setSuccessMessage(null);
    setErrorMessage(null);
    setDraftValues(targets);
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setErrorMessage(null);
    if (targets) {
      setDraftValues(targets);
    }
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!isEditing) return;

    setErrorMessage(null);
    setSuccessMessage(null);
    saveTargetsMutation.mutate(draftValues);
  };

  const handleFieldChange =
    (field: keyof TargetsFormValues) =>
    (value: string | number): void => {
      const numericValue = toOptionalNumber(value);
      setDraftValues((prev) => ({
        ...prev,
        [field]: typeof numericValue === "number" ? numericValue : null,
      }));
    };

  return (
    <Paper
      withBorder
      radius="md"
      p="xl"
      component="form"
      onSubmit={handleSubmit}
      style={{
        backgroundColor: "#FFFFFF",
        borderColor: "#E5E7EB",
        boxShadow: "0 12px 24px -20px rgba(15, 23, 42, 0.35)",
      }}
    >
      <Stack gap="xl">
        <Group justify="space-between" align="flex-start" gap="lg" wrap="wrap">
          <Stack gap={6} style={{ flex: "1 1 260px" }}>
            <Text size="xs" tt="uppercase" fw={600} style={{ letterSpacing: "0.1em", color: "#111827" }}>
              Daily targets
            </Text>
            <Text fw={600} size="lg" style={{ color: "#111827" }}>
              Calorie & macro goals
            </Text>
            <Text size="sm" style={{ color: "#4B5563", lineHeight: 1.6 }}>
              Keep a single sheet of record for where you want each day to land. Adjust these targets anytime as your
              training cycle evolves.
            </Text>
          </Stack>
          <Group gap="sm">
            {isEditing ? (
              <>
                <Button type="button" variant="subtle" onClick={handleCancelEdit} disabled={isSaving}>
                  Cancel
                </Button>
                <Button type="submit" loading={isSaving} style={{ backgroundColor: "#0F172A" }}>
                  Save targets
                </Button>
              </>
            ) : (
              <Button
                type="button"
                variant="outline"
                onClick={handleStartEdit}
                disabled={!targets || isLoadingTargets}
                style={{ borderColor: "#0F172A", color: "#0F172A" }}
              >
                Edit targets
              </Button>
            )}
          </Group>
        </Group>

        {successMessage && (
          <Alert
            variant="light"
            color="teal"
            icon={<IconCheck size={16} />}
            withCloseButton
            onClose={() => setSuccessMessage(null)}
          >
            {successMessage}
          </Alert>
        )}

        {errorMessage && (
          <Alert
            variant="light"
            color="red"
            icon={<IconAlertCircle size={16} />}
            withCloseButton
            onClose={() => setErrorMessage(null)}
          >
            {errorMessage}
          </Alert>
        )}

        {loadErrorMessage && (
          <Alert variant="light" color="red" icon={<IconAlertCircle size={16} />}>
            {loadErrorMessage}
          </Alert>
        )}

        {showLoader ? (
          <Group justify="center" py="lg">
            <Loader color="var(--mantine-color-blue-6)" />
          </Group>
        ) : isEditing ? (
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="lg">
            <NumberInput
              label="Calories (kcal/day)"
              placeholder="e.g. 2200"
              min={0}
              step={50}
              value={draftValues.caloriesDailyTarget ?? ""}
              onChange={handleFieldChange("caloriesDailyTarget")}
              styles={{
                input: {
                  borderColor: "#E5E7EB",
                },
              }}
            />
            <NumberInput
              label="Protein (g/day)"
              placeholder="e.g. 120"
              min={0}
              step={5}
              value={draftValues.proteinsDailyTarget ?? ""}
              onChange={handleFieldChange("proteinsDailyTarget")}
              decimalScale={2}
              styles={{
                input: {
                  borderColor: "#E5E7EB",
                },
              }}
            />
            <NumberInput
              label="Carbs (g/day)"
              placeholder="e.g. 220"
              min={0}
              step={5}
              value={draftValues.carbsDailyTarget ?? ""}
              onChange={handleFieldChange("carbsDailyTarget")}
              decimalScale={2}
              styles={{
                input: {
                  borderColor: "#E5E7EB",
                },
              }}
            />
            <NumberInput
              label="Fats (g/day)"
              placeholder="e.g. 65"
              min={0}
              step={1}
              value={draftValues.fatsDailyTarget ?? ""}
              onChange={handleFieldChange("fatsDailyTarget")}
              decimalScale={2}
              styles={{
                input: {
                  borderColor: "#E5E7EB",
                },
              }}
            />
          </SimpleGrid>
        ) : (
          <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} spacing="lg">
            {targetMetrics.map((metric) => (
              <div
                key={metric.key}
                style={{
                  border: "1px solid #E5E7EB",
                  borderRadius: 12,
                  padding: "18px 20px",
                  backgroundColor: "#F8FAFC",
                }}
              >
                <Stack gap={6}>
                  <Text size="xs" tt="uppercase" fw={600} style={{ letterSpacing: "0.08em", color: "#111827" }}>
                    {metric.label}
                  </Text>
                  <Text fw={600} size="xl" style={{ color: "#1F2933" }}>
                    {formatMetricValue(metric.key, targets?.[metric.key] ?? null)}
                  </Text>
                  <Text size="sm" style={{ color: "#4B5563" }}>
                    {metric.description}
                  </Text>
                  <Text size="xs" style={{ color: "#6B7280" }}>
                    {metric.unit}
                  </Text>
                </Stack>
              </div>
            ))}
          </SimpleGrid>
        )}
      </Stack>
    </Paper>
  );
};
